import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "components/Dialog";
import {
  Auth0AccountRole,
  MeetingAdminResponse,
  OrgToTadpoleTransactionResponse,
  PaymentsService,
  StudentToOrgTransactionResponse,
  StudentToOrgTransactionType,
  TadpoleToTutorTransactionResponse,
  TransactionType,
} from "client/openapi";
import moment from "moment";
import MeetingDialog from "components/MeetingDialog";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import {
  concatenateMeetingStudents,
  concatenateMeetingTutors,
} from "util/concatenateSubject";
import { useNavigate } from "react-router-dom";
import { FaClock } from "react-icons/fa6";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import { getTransactionStatusColor } from "./statusHelper";
import {
  studentToOrgTransactionTypeToHumanReadable,
  paymentMethodToHumanReadable,
} from "./index";

function TransactionDetailsPopup({
  transaction,
}: {
  transaction:
    | StudentToOrgTransactionResponse
    | TadpoleToTutorTransactionResponse
    | OrgToTadpoleTransactionResponse;
}) {
  const navigate = useNavigate();

  const [
    meetingsTiedToTadpoleToTutorTransaction,
    setMeetingsTiedToTadpoleToTutorTransaction,
  ] = useState<MeetingAdminResponse[]>([]);

  const [
    directPaymentsTiedToTadpoleToTutorTransaction,
    setDirectPaymentsTiedToTadpoleToTutorTransaction,
  ] = useState<TadpoleToTutorTransactionResponse[]>([]);

  const [
    tadpoleToTutorTransactionsTiedToPayroll,
    setTadpoleToTutorTransactionsTiedToPayroll,
  ] = useState<TadpoleToTutorTransactionResponse[]>([]);

  const [
    studentToOrgTransactionBeingRefunded,
    setStudentToOrgTransactionBeingRefunded,
  ] = useState<StudentToOrgTransactionResponse>();
  const [
    studentToOrgTransactionBeingRetried,
    setStudentToOrgTransactionBeingRetried,
  ] = useState<StudentToOrgTransactionResponse>();

  const [
    meetingsTiedToStudentToOrgTransaction,
    setMeetingsTiedToStudentToOrgTransaction,
  ] = useState<MeetingAdminResponse[]>([]);

  const [childTransactions, setChildTransactions] = useState<
    StudentToOrgTransactionResponse[]
  >([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (
      transaction.transaction_type === TransactionType.STUDENT_TO_ORGANIZATION
    ) {
      if (transaction.refunding_tadpole_transaction_id) {
        PaymentsService.getTransaction({
          transactionId: transaction.refunding_tadpole_transaction_id,
        })
          .then((res) => {
            setStudentToOrgTransactionBeingRefunded(
              res as StudentToOrgTransactionResponse
            );
            setLoading(false);
          })
          .catch((e) => {
            setError("Failed to load transaction being refunded.");
            setLoading(false);
          });
      } else {
        if (transaction.retrying_tadpole_transaction_id) {
          PaymentsService.getTransaction({
            transactionId: transaction.retrying_tadpole_transaction_id,
          })
            .then((res) => {
              setStudentToOrgTransactionBeingRetried(
                res as StudentToOrgTransactionResponse
              );
              setLoading(false);
            })
            .catch((e) => {
              setError("Failed to load transaction being retried.");
              setLoading(false);
            });
        } else {
          const stx = transaction as StudentToOrgTransactionResponse;
          if (
            stx.student_to_org_transaction_type ===
            StudentToOrgTransactionType.CHARGE_FOR_CREDITS
          ) {
            Promise.all([
              PaymentsService.getMeetingsByStudentToOrgTransaction({
                transactionId: transaction.id,
              }),
              PaymentsService.getChildTransactionsByGroup({
                groupTransactionId: transaction.id,
              }),
            ])
              .then(([meetings, children]) => {
                setMeetingsTiedToStudentToOrgTransaction(meetings);
                setChildTransactions(children);
                setLoading(false);
              })
              .catch(() => {
                setError("Failed to load transaction details.");
                setLoading(false);
              });
          } else {
            PaymentsService.getMeetingsByStudentToOrgTransaction({
              transactionId: transaction.id,
            })
              .then((res) => {
                setMeetingsTiedToStudentToOrgTransaction(res);
                setLoading(false);
              })
              .catch(() => {
                setError("Failed to load meetings.");
                setLoading(false);
              });
          }
        }
      }
    }

    if (transaction.transaction_type === TransactionType.TADPOLE_TO_TUTOR) {
      PaymentsService.getMeetingsByTadpoleToTutorTransaction({
        transactionId: transaction.id,
      })
        .then((res) => {
          setMeetingsTiedToTadpoleToTutorTransaction(res);
          setLoading(false);
        })
        .catch((e) => {
          setError("Failed to load meetings.");
          setLoading(false);
        });

      PaymentsService.getDirectPaymentsInAutomaticPaymentGroup({
        groupTransactionId: transaction.id,
      })
        .then((res) => {
          setDirectPaymentsTiedToTadpoleToTutorTransaction(res);
          setLoading(false);
        })
        .catch((e) => {
          setError("Failed to load direct payments.");
          setLoading(false);
        });
    }

    if (
      transaction.transaction_type === TransactionType.ORGANIZATION_TO_TADPOLE
    ) {
      PaymentsService.getTransactionsByFundingTransactionId({
        fundingTransactionId: transaction.id,
      })
        .then((res) => {
          setTadpoleToTutorTransactionsTiedToPayroll(res);
          setLoading(false);
        })
        .catch((e) => {
          setError("Failed to load transactions.");
          setLoading(false);
        });
    }
  }, [transaction]);

  console.log(
    transaction.transaction_type === TransactionType.TADPOLE_TO_TUTOR,
    [transaction]
  );

  // Parse date from pre-bill reason string
  // Format: "Pre-bill: Name Subject with Tutor on March 05, 2026, 11:00 AM EST (90 min)"
  const parseDateFromReason = (reason: string): moment.Moment | null => {
    const match = reason.match(/\bon\s+(.+?)\s*\(/);
    if (!match) return null;
    const dateStr = match[1].trim();
    const parsed = moment(dateStr, [
      "MMMM DD, YYYY, h:mm A z",
      "MMMM DD, YYYY, h:mm A",
    ]);
    return parsed.isValid() ? parsed : null;
  };

  const cancelledChildren = childTransactions.filter(
    (child) => child.meeting_student === null
  );

  const meetingsById = new Map(
    meetingsTiedToStudentToOrgTransaction.map((m) => [m.id, m])
  );

  const rescheduledChildren = childTransactions
    .filter((child) => {
      if (!child.meeting_student) return false;
      const meeting = meetingsById.get(child.meeting_student.meeting_id);
      if (!meeting) return false;
      const reasonDate = parseDateFromReason(child.reason || "");
      if (!reasonDate) return false;
      return !reasonDate.isSame(moment(meeting.start), "day");
    })
    .map((child) => ({
      child,
      meeting: meetingsById.get(child.meeting_student!.meeting_id)!,
    }));

  if (loading) {
    return (
      <DialogContent className="dialog-content max-w-[900px] p-6 bg-blue-50 rounded-md flex flex-col items-center space-y-4">
        {/* <div className="w-8 h-8 border-4 border-green-300 border-t-transparent border-solid rounded-full animate-spin"></div>
        <div className="text-blue-700 font-semibold">Loading meetings...</div> */}
      </DialogContent>
    );
  }

  if (error) {
    return (
      <DialogContent className="p-6 bg-red-50 rounded-md">
        <div className="text-red-700 font-semibold">{error}</div>
      </DialogContent>
    );
  }

  return (
    <>
      <DialogContent className="dialog-content max-w-[900px] p-6 bg-gray-50 rounded-md shadow-md">
        {transaction.transaction_type ===
          TransactionType.STUDENT_TO_ORGANIZATION && (
          <>
            {studentToOrgTransactionBeingRefunded && (
              <div className="mb-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Refunding the following transaction:
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Date
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Amount
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Type
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {moment(
                            studentToOrgTransactionBeingRefunded.timestamp
                          ).format("MMM Do YYYY, h:mma")}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                          ${studentToOrgTransactionBeingRefunded.amount / 100}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {(studentToOrgTransactionTypeToHumanReadable.find(
                            (t) =>
                              t.value ===
                              studentToOrgTransactionBeingRefunded.student_to_org_transaction_type
                          )?.label ?? "Unknown") +
                            (paymentMethodToHumanReadable.find(
                              (t) =>
                                t.value ===
                                studentToOrgTransactionBeingRefunded.payment_method_type
                            )?.label ?? "")}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {studentToOrgTransactionBeingRefunded.student_to_org_transaction_type !==
                            StudentToOrgTransactionType.DIRECT &&
                            studentToOrgTransactionBeingRefunded.student_to_org_transaction_type !==
                              StudentToOrgTransactionType.CHARGE_FOR_CREDITS && (
                              <Dialog>
                                <DialogTrigger>
                                  <Button
                                    color={ButtonColor.PURPLE}
                                    size={ButtonSize.EXTRA_SMALL}
                                    fill={ButtonFill.HOLLOW}
                                  >
                                    View Details
                                  </Button>
                                </DialogTrigger>
                                <TransactionDetailsPopup
                                  transaction={
                                    studentToOrgTransactionBeingRefunded
                                  }
                                />
                              </Dialog>
                            )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {studentToOrgTransactionBeingRetried && (
              <div className="mb-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Retrying the following transaction:
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Timestamp
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Amount
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Type
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Transaction
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="hover:bg-blue-50 transition-colors">
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {moment(
                            studentToOrgTransactionBeingRetried.timestamp
                          ).format("MMM Do YYYY, h:mma")}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                          ${studentToOrgTransactionBeingRetried.amount / 100}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {(studentToOrgTransactionTypeToHumanReadable.find(
                            (t) =>
                              t.value ===
                              studentToOrgTransactionBeingRetried.student_to_org_transaction_type
                          )?.label ?? "Unknown") +
                            (paymentMethodToHumanReadable.find(
                              (t) =>
                                t.value ===
                                studentToOrgTransactionBeingRetried.payment_method_type
                            )?.label ?? "")}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          <Dialog>
                            <DialogTrigger>
                              <Button
                                color={ButtonColor.PURPLE}
                                size={ButtonSize.EXTRA_SMALL}
                                fill={ButtonFill.HOLLOW}
                              >
                                View Details
                              </Button>
                            </DialogTrigger>
                            <TransactionDetailsPopup
                              transaction={studentToOrgTransactionBeingRetried}
                            />
                          </Dialog>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {meetingsTiedToStudentToOrgTransaction.length > 0 && (
              <div>
                <h2 className="text-xl mb-4 font-bold text-gray-800">
                  Meetings Tied to Transaction
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Date
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Time
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Meeting Name
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Students
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Tutors
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {meetingsTiedToStudentToOrgTransaction.map(
                        (meeting, index) => {
                          const start = moment(meeting.start);
                          const end = start
                            .clone()
                            .add(meeting.duration, "minutes");
                          return (
                            <tr
                              key={meeting.id}
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              } hover:bg-blue-50 transition-colors`}
                            >
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                                {start.format("ddd, MMM Do")}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                                {start.format("h:mma")} - {end.format("h:mma")}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {meeting.name}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {concatenateMeetingStudents(meeting.students)}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {concatenateMeetingTutors(meeting.tutors)}
                              </td>
                              <td className="py-3 px-4 text-blue-500 border-b border-gray-200">
                                <Dialog>
                                  <DialogTrigger>
                                    <Button
                                      color={ButtonColor.SKYBLUE}
                                      size={ButtonSize.EXTRA_SMALL}
                                      fill={ButtonFill.HOLLOW}
                                      className="text-sm"
                                    >
                                      View
                                    </Button>
                                  </DialogTrigger>
                                  <MeetingDialog
                                    isCreate={false}
                                    role={Auth0AccountRole.ORG_TUTOR}
                                    event={meeting}
                                    setEvents={() => Promise.resolve()}
                                  />
                                </Dialog>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {rescheduledChildren.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl mb-4 font-bold text-amber-700">
                  Rescheduled Meetings
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-amber-100 to-amber-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Original Meeting Details
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          New Date
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rescheduledChildren.map(({ child, meeting }, index) => (
                        <tr
                          key={child.id}
                          className={`${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          } hover:bg-amber-50 transition-colors`}
                        >
                          <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                            {child.reason}
                          </td>
                          <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                            {moment(meeting.start).format("MMMM DD, YYYY")}
                          </td>
                          <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                            ${(child.amount / 100).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {cancelledChildren.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl mb-4 font-bold text-red-700">
                  Cancelled Meetings
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-red-100 to-red-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Original Meeting Details
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {cancelledChildren.map((child, index) => (
                        <tr
                          key={child.id}
                          className={`${
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          } hover:bg-red-50 transition-colors`}
                        >
                          <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                            {child.reason}
                          </td>
                          <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                            ${(child.amount / 100).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        {transaction.transaction_type === TransactionType.TADPOLE_TO_TUTOR && (
          <>
            {meetingsTiedToTadpoleToTutorTransaction.length > 0 && (
              <div>
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Meetings Tied to Transaction
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Date
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Time
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Meeting Name
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Students
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Tutors
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {meetingsTiedToTadpoleToTutorTransaction.map(
                        (meeting, index) => {
                          const start = moment(meeting.start);
                          const end = start
                            .clone()
                            .add(meeting.duration, "minutes");
                          return (
                            <tr
                              key={meeting.id}
                              className={`${
                                index % 2 === 0 ? "bg-white" : "bg-gray-50"
                              } hover:bg-blue-50 transition-colors`}
                            >
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                                {start.format("ddd, MMM Do")}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                                {start.format("h:mma")} - {end.format("h:mma")}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {meeting.name}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {concatenateMeetingStudents(meeting.students)}
                              </td>
                              <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                                {concatenateMeetingTutors(meeting.tutors)}
                              </td>
                              <td className="py-3 px-4 text-blue-500 border-b border-gray-200">
                                <Dialog>
                                  <DialogTrigger>
                                    <Button
                                      color={ButtonColor.SKYBLUE}
                                      size={ButtonSize.EXTRA_SMALL}
                                      fill={ButtonFill.HOLLOW}
                                      className="text-sm"
                                    >
                                      View
                                    </Button>
                                  </DialogTrigger>
                                  <MeetingDialog
                                    isCreate={false}
                                    role={Auth0AccountRole.ORG_TUTOR}
                                    event={meeting}
                                    setEvents={() => Promise.resolve()}
                                  />
                                </Dialog>
                              </td>
                            </tr>
                          );
                        }
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {directPaymentsTiedToTadpoleToTutorTransaction.length > 0 && (
              <div className="mt-6">
                <h2 className="text-xl font-bold mb-4 text-gray-800">
                  Direct Payments Tied to Automatic Payment
                </h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                    <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                      <tr>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Timestamp
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Amount
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Type
                        </th>
                        <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                          Reason
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {directPaymentsTiedToTadpoleToTutorTransaction.map(
                        (payment, index) => (
                          <tr
                            key={payment.id}
                            className={`${
                              index % 2 === 0 ? "bg-white" : "bg-gray-50"
                            } hover:bg-blue-50 transition-colors`}
                          >
                            <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                              {moment(payment.timestamp).format(
                                "MMM Do YYYY, h:mma"
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                              ${payment.amount / 100}
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                              Direct Payment
                            </td>
                            <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                              {payment.reason}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
        {transaction.transaction_type ===
          TransactionType.ORGANIZATION_TO_TADPOLE && (
          <div>
            <h2 className="text-xl font-bold mb-4 text-gray-800">
              Transactions Tied to Payroll
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full border-collapse border border-gray-200 bg-white rounded-md shadow-sm">
                <thead className="bg-gradient-to-r from-blue-100 to-blue-50 text-gray-700">
                  <tr>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Amount
                    </th>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Timestamp
                    </th>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Type
                    </th>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Tutor
                    </th>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Status
                    </th>
                    <th className="py-3 px-4 text-left font-semibold border-b border-gray-200">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tadpoleToTutorTransactionsTiedToPayroll.map(
                    (transaction, index) => (
                      <tr
                        key={transaction.id}
                        className={`${
                          index % 2 === 0 ? "bg-white" : "bg-gray-50"
                        } hover:bg-blue-50 transition-colors`}
                      >
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200 whitespace-nowrap">
                          ${transaction.amount / 100}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          {moment(transaction.timestamp).format(
                            "MMM Do YYYY, h:mma"
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          Automatic Payroll Payment
                        </td>
                        <td className="py-3 px-4 text-gray-800 border-b border-gray-200">
                          <Button
                            onClick={() => {
                              navigate(
                                `/manage/tutors/${transaction.tutor_id}`
                              );
                            }}
                            color={ButtonColor.SKYBLUE}
                            size={ButtonSize.EXTRA_SMALL}
                            fill={ButtonFill.HOLLOW}
                          >
                            {transaction.tutor.first_name}{" "}
                            {transaction.tutor.last_name}
                          </Button>
                        </td>
                        <td className="py-3 px-4 pr-7 text-gray-800 border-b border-gray-200">
                          {(() => {
                            const statusColor = getTransactionStatusColor(
                              transaction.status
                            );
                            switch (statusColor) {
                              case "green":
                                return (
                                  <IoCheckmarkCircle className="mx-auto green-mark" />
                                );
                              case "purple":
                                return (
                                  <IoCheckmarkCircle className="mx-auto purple-mark" />
                                );
                              case "orange":
                                return (
                                  <FaClock
                                    className="mx-auto"
                                    style={{
                                      color: "#FFB341",
                                      fontSize: "20px",
                                    }}
                                  />
                                );
                              case "red":
                                return (
                                  <IoCloseCircle className="mx-auto red-mark" />
                                );
                              default:
                                return (
                                  <FaClock
                                    className="mx-auto"
                                    style={{
                                      color: "lightblue",
                                      fontSize: "20px",
                                    }}
                                  />
                                );
                            }
                          })()}
                        </td>

                        <td className="py-3 px-4 text-blue-500 border-b border-gray-200">
                          <Dialog>
                            <DialogTrigger>
                              <Button
                                color={ButtonColor.PURPLE}
                                size={ButtonSize.EXTRA_SMALL}
                                fill={ButtonFill.HOLLOW}
                              >
                                View Details
                              </Button>
                            </DialogTrigger>
                            <TransactionDetailsPopup
                              transaction={transaction}
                            />
                          </Dialog>
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </>
  );
}

export default TransactionDetailsPopup;
